/**
 * Sound Effects API Service
 *
 * Handles AI sound effect generation from text or video.
 * Supports multiple providers: HunyuanVideo-Foley, LTX-2 Audio, AudioCraft, Stable Audio.
 */

import type { SfxProvider } from '../constants/soundEffects';
import { getApiBaseUrl } from './urlResolver';
import { getAuthHeadersFromStorage, handleResponse } from '../utils/httpUtils';
import { getJobStatus } from './jobs';

// =============================================================================
// REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Text-to-audio generation request
 */
export interface SfxGenerationRequest {
  provider: SfxProvider;
  prompt: string;
  duration: number;
  seed?: number;
}

/**
 * Video-to-audio generation request
 */
export interface SfxVideoToAudioRequest {
  provider: SfxProvider;
  video_file: File;
  prompt?: string;
  duration?: number;
  seed?: number;
}

/**
 * Sound effect generation response
 */
export interface SfxGenerationResponse {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
}

/**
 * Job status response
 */
export interface SfxJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  result?: {
    audio_url: string;
    duration_seconds?: number;
    sample_rate?: number;
    format?: string;
    video_url?: string; // For unified audio+video providers
  };
  error?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Provider list item — mirrors GET /sound-effects/providers exactly
 * (shared/routes/sound_effects.py::get_providers).
 *
 * This interface used to describe a response the backend never sent (`id`,
 * `type`, `capabilities`, `sample_rate`, `quality`, `env_var`), which forced
 * callers to cast to `Record<string, unknown>` just to read `provider_id`.
 */
export interface SfxProviderListItem {
  provider_id: string;
  name: string;
  enabled: boolean;
  supports_t2a: boolean;
  supports_v2a: boolean;
  min_duration: number;
  max_duration: number;
  default_duration: number;
  quality_rating: number;
  description: string;
}

/**
 * Providers list response
 */
export interface SfxProvidersResponse {
  providers: SfxProviderListItem[];
}

// =============================================================================
// API SERVICE
// =============================================================================

export class SoundEffectsAPI {
  /**
   * Get available sound effect providers
   * @returns List of available providers with capabilities
   */
  static async getSfxProviders(): Promise<SfxProvidersResponse> {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/sound-effects/providers`, {
      method: 'GET',
      headers: await getAuthHeadersFromStorage(),
    });

    return handleResponse<SfxProvidersResponse>(response);
  }

  /**
   * Submit text-to-audio sound effect generation job
   * @param request - Generation request
   * @returns Job submission response with job_id
   */
  static async submitSfxGeneration(request: SfxGenerationRequest): Promise<SfxGenerationResponse> {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/sound-effects/generate/text`, {
      method: 'POST',
      headers: await getAuthHeadersFromStorage(),
      body: JSON.stringify({
        provider: request.provider,
        prompt: request.prompt,
        duration_seconds: request.duration,
        seed: request.seed ?? -1,
      }),
    });

    return handleResponse<SfxGenerationResponse>(response);
  }

  /**
   * Submit video-to-audio sound effect generation job
   * @param provider - Provider to use
   * @param videoFile - Video file to generate audio from
   * @param options - Optional generation parameters
   * @returns Job submission response with job_id
   */
  static async submitSfxFromVideo(
    _provider: SfxProvider,
    videoFile: File,
    options?: {
      prompt?: string;
      duration?: number;
      seed?: number;
    }
  ): Promise<SfxGenerationResponse> {
    const baseUrl = getApiBaseUrl();

    // Convert video file to base64 data URI (backend expects video_url as JSON string)
    const videoBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(videoFile);
    });

    const response = await fetch(`${baseUrl}/api/v1/sound-effects/generate/video-to-audio`, {
      method: 'POST',
      headers: await getAuthHeadersFromStorage(),
      body: JSON.stringify({
        video_url: videoBase64,
        prompt: options?.prompt ?? '',
        duration_seconds: options?.duration ?? 10.0,
        seed: options?.seed ?? -1,
      }),
    });

    return handleResponse<SfxGenerationResponse>(response);
  }

  /**
   * Get status of a sound effect generation job
   * @param jobId - Job ID returned from submit
   * @returns Job status with progress and result when completed
   */
  static async getSfxJobStatus(jobId: string): Promise<SfxJobStatus> {
    // ONE polling endpoint (integrationMode Stage 01): the "sound_effects"
    // store is registered with the unified job registry, so the per-capability
    // /sound-effects/status/{id} route it used to call was a pure duplicate.
    // That route's only distinct behaviour was rescaling progress from the
    // canonical 0-100 to a private 0-1; the mapping below is the single
    // conversion point, and it now converts nothing.
    const job = await getJobStatus(jobId);
    const extra = (job.extra_metadata ?? {}) as Record<string, unknown>;
    const audioUrl = job.result_url ?? (extra.audio_url as string | undefined);

    return {
      job_id: job.job_id,
      status: job.status as SfxJobStatus['status'],
      progress: job.progress,
      message: job.message,
      error: job.error ?? undefined,
      result: audioUrl
        ? {
            audio_url: audioUrl,
            duration_seconds: extra.duration_seconds as number | undefined,
            sample_rate: extra.sample_rate as number | undefined,
            format: extra.format as string | undefined,
            video_url: extra.video_url as string | undefined,
          }
        : undefined,
    };
  }

  /**
   * Poll job status until completion
   * @param jobId - Job ID to poll
   * @param onProgress - Optional progress callback
   * @param pollInterval - Polling interval in ms (default: 2000)
   * @param maxAttempts - Maximum polling attempts (default: 150 = 5 minutes)
   * @returns Final job result
   */
  static async pollJobStatus(
    jobId: string,
    onProgress?: (progress: number, message?: string) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 150,
    initialProgress: number = 0
  ): Promise<SfxJobStatus> {
    let attempts = 0;
    let maxProgress = initialProgress;

    while (attempts < maxAttempts) {
      try {
        const status = await this.getSfxJobStatus(jobId);

        // Progress is 0-100 on both sides now (the unified registry's canonical
        // scale). Track max to prevent the progress bar going backwards.
        if (onProgress) {
          const progressPercent = status.progress ?? 0;
          maxProgress = Math.max(maxProgress, progressPercent);
          onProgress(maxProgress, status.message);
        }

        // Check if job is complete
        if (status.status === 'completed') {
          if (onProgress) onProgress(100, 'Sound effect generation complete');
          return status;
        }

        if (status.status === 'failed') {
          throw new Error(status.error || 'Sound effect generation failed');
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        attempts++;
      } catch (error) {
        // If it's a failed status, throw immediately
        if (error instanceof Error && error.message.includes('failed')) {
          throw error;
        }
        // Continue polling on transient errors
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        attempts++;
      }
    }

    throw new Error('Sound effect generation timed out');
  }

  /**
   * Generate sound effect from text (full flow: submit + poll)
   * @param request - Generation request
   * @param onProgress - Optional progress callback
   * @returns Final result with audio URL
   */
  static async generateSfx(
    request: SfxGenerationRequest,
    onProgress?: (progress: number, message?: string) => void
  ): Promise<SfxJobStatus> {
    // Submit the job
    const submitResponse = await this.submitSfxGeneration(request);

    if (onProgress) onProgress(10, 'Job submitted, processing...');

    // Poll for completion (start from 10% to prevent regression)
    return this.pollJobStatus(submitResponse.job_id, onProgress, 2000, 150, 10);
  }

  /**
   * Generate sound effect from video (full flow: submit + poll)
   * @param provider - Provider to use
   * @param videoFile - Video file
   * @param options - Optional generation parameters
   * @param onProgress - Optional progress callback
   * @returns Final result with audio URL
   */
  static async generateSfxFromVideo(
    provider: SfxProvider,
    videoFile: File,
    options?: {
      prompt?: string;
      duration?: number;
      seed?: number;
    },
    onProgress?: (progress: number, message?: string) => void
  ): Promise<SfxJobStatus> {
    // Submit the job
    const submitResponse = await this.submitSfxFromVideo(provider, videoFile, options);

    if (onProgress) onProgress(10, 'Video uploaded, processing...');

    // Poll for completion (start from 10% to prevent regression)
    return this.pollJobStatus(submitResponse.job_id, onProgress, 2000, 150, 10);
  }
}

export default SoundEffectsAPI;
